import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Users, MessageSquare, Bell, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface MinorAgentRulesCardProps {
  hasParentConnected?: boolean;
  className?: string;
}

const MinorAgentRulesCard: React.FC<MinorAgentRulesCardProps> = ({ 
  hasParentConnected = false,
  className = '' 
}) => {
  const navigate = useNavigate();

  return (
    <Card className={`bg-gradient-to-br from-amber-900/40 to-amber-950/60 border-amber-600/50 ${className}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-amber-200 flex items-center gap-2 text-lg">
          <Shield className="h-5 w-5" />
          Agent & NIL Communication Rules
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-amber-100/80 text-sm">
          For your safety, agents and NIL representatives cannot contact you directly. 
          Here's how it works:
        </p>

        <div className="space-y-2">
          <div className={`flex items-start gap-2 p-2 rounded-md ${hasParentConnected ? 'bg-green-900/30' : 'bg-red-900/30'}`}>
            <Users className={`h-4 w-4 mt-0.5 ${hasParentConnected ? 'text-green-400' : 'text-red-400'}`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${hasParentConnected ? 'text-green-200' : 'text-red-200'}`}>
                {hasParentConnected ? '✓ Parent Connected' : '✗ No Parent Connected'}
              </p>
              <p className="text-xs text-amber-100/60">
                {hasParentConnected 
                  ? 'Your parent can receive agent communications on your behalf.'
                  : 'Share your Connect ID with your parent to enable agent communications.'}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2 p-2 rounded-md bg-amber-900/20">
            <MessageSquare className="h-4 w-4 mt-0.5 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-200">Communication Flow</p>
              <p className="text-xs text-amber-100/60">
                Agents contact your parent first. Your parent can add you to conversations.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2 p-2 rounded-md bg-amber-900/20">
            <Bell className="h-4 w-4 mt-0.5 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-200">Interest Alerts</p>
              <p className="text-xs text-amber-100/60">
                When someone is interested, you'll see "Someone looks interested, reach out to your Parent(s)!"
              </p>
            </div>
          </div>
        </div>

        {!hasParentConnected && (
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full border-amber-500 text-amber-200 hover:bg-amber-900/30"
            onClick={() => navigate('/settings')}
          >
            <Info className="h-4 w-4 mr-2" />
            View Connect ID
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default MinorAgentRulesCard;
